import { BillingInvoice, BillingInvoiceAdjustment, FocusFieldInfo, NamedFilterSet } from '@apis/Invoices/model';
import { LineItemCompactToken } from '@root/Components/Filter/Design';
import { SettingsSectionItem, SettingsSectionItemBody, SettingsSectionItemHeader, SettingsSectionItemHeaderLabel } from '@root/Design/Settings';
import { useEffect, useMemo, useState } from 'react';
import { Filter as FilterIcon, InfoCircle, Plus, Trash, ChevronDown, ChevronRight } from 'tabler-icons-react';
import { ApplyToSection } from './Components';
import { ActionIcon, Group, TextInput, NumberInput, Select, Space } from '@mantine/core';
import { useToggle } from '@root/Services/EventEmitter';
import { DatePicker } from '@mantine/dates';
import { Combobox } from '../Picker/Combobox';
import { useDi, useDiMemo } from '@root/Services/DI';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { queryBuilder } from '@root/Services/QueryExpr';
import { FocusMetadataService } from '@root/Services/Invoices/FocusMetadataService';
import { TooltipWhite } from '@root/Design/Primitives';

export interface OverrideUpdateLine {
    Field: string;
    Value: string;
    ApplyTo?: NamedFilterSet | null;
}

export function OverrideUpdatesEditor(props: { invoice: BillingInvoice; value: string | null; onChange: (value: string | null) => void }) {
    const focusFieldSvc = useDi(FocusMetadataService);
    const [focusFields, setFocusFields] = useState<FocusFieldInfo[]>([]);

    useEffect(() => {
        (async () => {
            const metadata = await focusFieldSvc.getFocusMetadata();
            const ignoredFields = [
                'BilledCost',
                'EffectiveCost',
                'ContractedCost',
                'ChargePeriodStart',
                'ChargePeriodEnd',
                'BillingPeriodStart',
                'BillingPeriodEnd',
            ];
            setFocusFields(
                metadata
                    .getFields()
                    .filter((f) => !f.Ignore)
                    .filter((f) => !ignoredFields.includes(f.Field!))
            );
        })();
    }, []);

    const lines: OverrideUpdateLine[] = useMemo(() => (props.value ? JSON.parse(props.value) : []), [props.value]);

    const setLines = (updated: OverrideUpdateLine[]) => {
        props.onChange(updated.length > 0 ? JSON.stringify(updated) : null);
    };

    const fieldOptions = useMemo(
        () =>
            focusFields.map((f) => ({
                value: f.Field!,
                label: f.DisplayName!,
            })),
        [focusFields]
    );

    const addLine = () => {
        setLines([...lines, { Field: '', Value: '', ApplyTo: null }]);
    };

    const updateLine = (idx: number, patch: Partial<OverrideUpdateLine>) => {
        setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    };

    const removeLine = (idx: number) => {
        setLines(lines.filter((_, i) => i !== idx));
    };

    const getFocusField = (fieldName: string) => focusFields.find((f) => f.Field === fieldName);
    const [collapsed, { toggle }] = useToggle(false);
    const ExpanderIcon = collapsed ? ChevronRight : ChevronDown;

    return (
        <>
            <SettingsSectionItemHeader>
                <div style={{ width: '100%' }}>
                    <Group spacing={4} sx={{ float: 'left' }}>
                        <TooltipWhite
                            withinPortal
                            multiline
                            label={`(Optional) Add filters to limit which invoice line items are applicable for this tile.`}
                        >
                            <ActionIcon>
                                <InfoCircle size={16} />
                            </ActionIcon>
                        </TooltipWhite>
                        <SettingsSectionItemHeaderLabel>Overwrite Fields for Line Items</SettingsSectionItemHeaderLabel>
                    </Group>

                    <ActionIcon sx={{ float: 'right' }} onClick={toggle} variant="transparent">
                        <ExpanderIcon size={16} />
                    </ActionIcon>
                </div>
            </SettingsSectionItemHeader>
            <SettingsSectionItemBody hidden={collapsed}>
                {lines.map((line, idx) => (
                    <>
                        <OverrideLineItem
                            key={idx}
                            line={line}
                            invoice={props.invoice}
                            fieldOptions={fieldOptions}
                            focusField={getFocusField(line.Field)}
                            onUpdate={(p) => updateLine(idx, p)}
                            onRemove={() => removeLine(idx)}
                        />
                        <Space h="sm" />
                    </>
                ))}
                <LineItemCompactToken onClick={addLine}>
                    <Group spacing={4}>
                        <Plus size={16} /> Add Override
                    </Group>
                </LineItemCompactToken>
            </SettingsSectionItemBody>
        </>
    );
}

function OverrideLineItem(props: {
    line: OverrideUpdateLine;
    invoice: BillingInvoice;
    fieldOptions: { value: string; label: string }[];
    focusField: FocusFieldInfo | undefined;
    onUpdate: (p: Partial<OverrideUpdateLine>) => void;
    onRemove: () => void;
}) {
    const { line, invoice, fieldOptions, focusField, onUpdate, onRemove } = props;
    const invoiceApi = useDiMemo(InvoiceApiService);
    const [showFilter, { toggle: toggleFilter }] = useToggle(!!line.ApplyTo);

    const stringOptions = useMemo(() => {
        if (!focusField || focusField.IndexerType !== 'string') return async () => [];

        const invoiceDate = new Date(invoice.Year!, invoice.Month! - 1, 1);
        return async () => {
            const results = await queryBuilder<Record<string, string>>()
                .select((b) => ({
                    count: b.count(),
                    value: b.model[focusField.Field!],
                }))
                .execute((q) => invoiceApi.queryMonthlyRollup(q, [invoiceDate]));
            return (results.Results ?? [])
                .sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }))
                .map((item) => ({ value: item.value }));
        };
    }, [focusField, invoice]);

    const fieldComboOptions = useMemo(() => {
        return async () =>
            fieldOptions.map((opt) => {
                return { value: opt.value, label: opt.label };
            });
    }, [fieldOptions]);

    const inputType = (focusField?.IndexerType ?? 'string') as 'string' | 'date' | 'float';

    const valueInput =
        inputType === 'string' ? (
            <Combobox
                options={stringOptions}
                lazyLoad
                pickerProps={{ inlineFullText: true }}
                value={line.Value}
                onChange={(v) => onUpdate({ Value: v })}
            />
        ) : inputType === 'date' ? (
            <DatePicker value={line.Value ? new Date(line.Value) : null} onChange={(d) => onUpdate({ Value: d?.toISOString() ?? '' })} />
        ) : inputType === 'float' ? (
            <NumberInput value={parseFloat(line.Value) || 0} onChange={(v) => onUpdate({ Value: String(v ?? 0) })} />
        ) : (
            <TextInput value={line.Value} onChange={(e) => onUpdate({ Value: e.target.value })} placeholder="Enter value" />
        );

    return (
        <SettingsSectionItem>
            <SettingsSectionItemHeader>
                <Combobox options={fieldComboOptions} value={line.Field} onChange={(v) => onUpdate({ Field: v ?? '' })} />
                <div style={{ flex: 1 }}>{valueInput}</div>
                <ActionIcon variant="subtle" onClick={toggleFilter} title={showFilter ? 'Hide filter' : 'Add filter'}>
                    <FilterIcon size={16} />
                </ActionIcon>
                <ActionIcon variant="subtle" onClick={onRemove}>
                    <Trash size={16} />
                </ActionIcon>
            </SettingsSectionItemHeader>
            {showFilter && (
                <SettingsSectionItemBody style={{ margin: 'calc(-1 * var(--settings-body-padding))' }}>
                    <ApplyToSection
                        adjustment={
                            {
                                ApplyTo: line.ApplyTo,
                                AdjustmentOperation: 'OverrideItems',
                            } as BillingInvoiceAdjustment
                        }
                        billingInvoice={invoice}
                        showCollapseHeader={false}
                        onChanges={(updated) => onUpdate({ ApplyTo: updated.ApplyTo })}
                    />
                </SettingsSectionItemBody>
            )}
        </SettingsSectionItem>
    );
}
